'use client'

import { OtpForm } from '@/components/auth/otp-form'
import { toast } from '@/components/common/toast'
import { ImageCM } from '@/components/extension/image-cm'
import { Divider } from '@/components/ui/divider'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  useRefreshAfterLogin,
  useUserQuery,
} from '@/hooks/react-query/user/use-user'
import { __cAuthService } from '@/services/client/__c.auth.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useBoolean } from 'usehooks-ts'
import * as z from 'zod'
import { useTurnstile } from '@/hooks/useTurnstile'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

import { useConsentManager } from '@c15t/nextjs'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { cn, getValue, validatePhone } from '@/lib/utils'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import Link from 'next/link'
import { Checkbox } from '../ui/checkbox'
import { RANKING_LABEL_VN } from '@/types/auth'
import { useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/navigation'
import { SessionStorage } from '@/lib/utils/session-storage'
import { useSearchParams } from 'next/navigation'
import { useCallback, useState } from 'react'
import { ForgotPasswordForm } from './forgot-password-form'
import { openLogin } from './open-auth'
import QRLogin from './qr-login'

export function RegisterForm({
  isPage,
  callback,
}: {
  isPage?: boolean
  callback?: () => void
}) {
  const { refetch: refetchUser } = useUserQuery()
  const loading = useBoolean()
  const router = useRouter()
  const searchParams = useSearchParams()
  const trackEvent = useTrackEvent()
  const refreshAfterLogin = useRefreshAfterLogin()
  const t = useTranslations('login')
  const [isQrMode, setIsQrMode] = useState(false)
  const { unstable_acceptPolicyConsent } = useConsentManager()
  const [privacyChecked, setPrivacyChecked] = useState(false)
  const [privacyError, setPrivacyError] = useState(false)
  const { TurnstileWidget, isReady, verify, reset } = useTurnstile({
    action: 'register',
  })

  const formSchema = z.object({
    fullname: z.string().min(6, t('required_fullname')),
    phone: z.string().refine(
      (value) => {
        const phoneValidate = validatePhone(value)
        return phoneValidate.valid
      },
      {
        message: t('invalid_phone'),
      }
    ),
    email: z.string().email(t('invalid_email')),
    password: z.string().min(6, t('password_too_short')),
  })

  const handleToggleMode = useCallback(() => {
    setIsQrMode((prev) => !prev)
  }, [])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      fullname: '',
      phone: '',
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!privacyChecked) {
      setPrivacyError(true)
      return
    }

    loading.setTrue()
    try {
      const turnstileRes = await verify()
      if (!turnstileRes.success) {
        toast.message({
          title: t('error'),
          message: 'Vui lòng xác thực robot',
        })
        reset()
        loading.setFalse()
        return
      }

      const res = await __cAuthService.register(values)

      if (res?.status && res.status === 200) {
        toast.message({
          title: t('register_success'),
          message: res.message,
        })

        // Get user ID from response
        const registeredUserId = getValue(res, 'data.user_id', '')
        if (registeredUserId) {
          // Sử dụng showPopup trực tiếp với OtpForm thay vì qua showOTPModal
          setTimeout(() => {
            showPopup(
              <OtpForm
                phone={values.phone}
                onSuccess={processAfterLoginSuccess}
                type='register'
                isAuth={true}
                bodyAuth={{
                  user_id: registeredUserId,
                  fullname: values.fullname,
                  email: values.email,
                  phone: values.phone,
                }}
                onClose={() => hidePopup()}
              />
            )
          }, 100)
        }
      } else {
        toast.message({
          title: t('register_fail'),
          message: res.message,
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.setFalse()
      reset()
    }
  }

  const processAfterLoginSuccess = async () => {
    refreshAfterLogin()

    const { data: user } = await refetchUser()

    if (user) {
      trackEvent({
        event: ETrackingEvent.UserRegister,
        user_id: user.user_id,
        user_type: RANKING_LABEL_VN[user?.ranking?.ranking_id],
      })

      if (privacyChecked) {
        unstable_acceptPolicyConsent({
          type: 'privacy_policy',
          policyHash: 'privacy-policy-150626',
          externalId: user.id,
          identityProvider: 'coolmate',
        }).catch(() => {})
      }
    }
    if (isPage) {
      const redirectUrl =
        searchParams.get('redirect') ||
        SessionStorage.get<string>('auth_redirect_url')
      SessionStorage.remove('auth_redirect_url')
      hidePopup()
      const isInternal =
        !!redirectUrl &&
        redirectUrl.startsWith('/') &&
        !redirectUrl.startsWith('//')
      router.replace(isInternal ? redirectUrl : '/')
      return
    }

    callback?.()
    hidePopup()
  }

  if (isQrMode) {
    return <QRLogin onBack={handleToggleMode} />
  }

  return (
    <>
      <div className='text-neutral-800'>
        <ImageCM
          src={'/March2024/mceclip4_81.jpg'}
          width={132}
          height={22}
          alt={t('logo_alt')}
        />
        <h3 className='mt-4 font-criteria text-[28px] font-medium leading-8 tracking-normal text-neutral-900'>
          {t('register_privileges')}
        </h3>
        <p className='mt-3.5 font-sans text-xs'>{t('register_benefit')}</p>
        <div className='mb-4 mt-1 grid grid-cols-3 gap-3'>
          <ImageCM
            src={'/March2024/mceclip3_52.jpg'}
            width={112}
            height={44}
            className='w-full object-cover'
            alt={t('voucher_alt')}
          />
          <ImageCM
            src={'/March2024/mceclip1_36.jpg'}
            width={112}
            height={44}
            className='w-full object-cover'
            alt={t('gift_alt')}
          />
          <ImageCM
            src={'/March2024/mceclip2_55.jpg'}
            width={112}
            height={44}
            className='w-full object-cover'
            alt={t('cashback_alt')}
          />
        </div>
        <p className='mb-3 mt-4 font-sans text-xs font-bold'>
          {t('login_or_register_free')}
        </p>
        <div className='flex gap-3'>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
          >
            <ImageCM
              src={'/September2023/mceclip1_21.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login google'
            />
          </Button>
          <Button
            variant={'outline'}
            size={'icon'}
            className='rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50'
          >
            <ImageCM
              src={'/September2023/mceclip0_86.png'}
              width={26}
              height={26}
              className='h-full w-full object-scale-down'
              alt='Login facebook'
            />
          </Button>
          <Popover defaultOpen>
            <PopoverTrigger asChild>
              <Button
                variant={'outline'}
                size={'icon'}
                className={cn(
                  'rounded-lg p-1.5 hover:bg-neutral-50 active:bg-neutral-50',
                  isQrMode ? 'border-2 hover:border-primary-500' : ''
                )}
                onClick={handleToggleMode}
                disabled={form.formState.isSubmitting}
              >
                <ImageCM
                  src={'/2026/01/22/mceclip0_53.png'}
                  width={26}
                  height={26}
                  className='h-full w-full object-scale-down'
                  alt='Login Zalo Mini App'
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side='top'
              className='w-fit max-w-[200px] border-none bg-neutral-900 p-2 text-center text-xs text-white'
            >
              Quét mã QR trên Zalo Mini App của Coolmate, hoặc camera điện thoại
              <PopoverArrow
                className='fill-neutral-900'
                width={12}
                height={12}
              />
            </PopoverContent>
          </Popover>
        </div>
        <Divider text={t('or')} />
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <div className='flex items-start justify-between gap-3'>
            <FormField
              control={form.control}
              name='fullname'
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        autoFocus
                        floating
                        label={t('fullname')}
                        {...field}
                        error={form.formState.errors?.fullname}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        floating
                        label={t('phone')}
                        {...field}
                        error={form.formState.errors?.phone}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      floating
                      label={t('email')}
                      {...field}
                      error={form.formState.errors?.email}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    floating
                    type='password'
                    {...field}
                    label={t('password')}
                    error={form.formState.errors?.password}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className='flex items-center gap-2'>
            <Checkbox
              id='privacy-checkbox'
              checked={privacyChecked}
              onCheckedChange={(checked) => {
                setPrivacyChecked(!!checked)
                if (checked) setPrivacyError(false)
              }}
              className={privacyError ? 'border-red-500' : ''}
            />
            <label
              htmlFor='privacy-checkbox'
              className={`cursor-pointer font-sans text-xs ${privacyError ? 'text-red-500' : 'text-neutral-700'}`}
            >
              {t('privacy_checkbox')}
              <Link
                href='/page/chinh-sach-bao-mat-thong-tin-ca-nhan'
                className={`underline hover:opacity-90 ${privacyError ? 'text-red-500' : 'text-primary'}`}
                target='_blank'
              >
                {t('privacy_checkbox_link')}
              </Link>
            </label>
          </div>

          <div className='flex justify-center'>{TurnstileWidget}</div>

          <Button
            type='submit'
            className='w-full'
            variant='black'
            loading={loading.value}
            disabled={!isReady || loading.value}
            ga-tracking-value={`register_account`}
            ga-tracking-label='register_account'
          >
            {t('register_account')}
          </Button>
        </form>
      </Form>
      <div className='mt-4 flex justify-between'>
        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/login')
            } else {
              openLogin({ callback })
            }
          }}
        >
          {t('login')}
        </Button>

        <Button
          variant='flat'
          className='h-fit p-0 font-sans text-xs font-medium normal-case'
          onClick={() => {
            if (isPage) {
              router.push('/forgot-password')
            } else {
              showPopup(<ForgotPasswordForm />)
            }
          }}
        >
          {t('forgot_password')}
        </Button>
      </div>
    </>
  )
}
